import { DateTime } from 'luxon'
import { get } from 'lodash'
import { toThreeLetterTimezone } from 'utils/timezones'

// "Thu, Oct 29 · 2:00 PM – 3:00 PM EDT". Returns null when the slot is not a
// valid date so callers can hide the row instead of printing "Invalid DateTime".
export const formatSlotRange = (startDateTime, durationInMinutes, timezone) => {
  if (!startDateTime) return null
  const start = DateTime.fromISO(startDateTime, { zone: timezone }).setLocale(
    'en-US',
  )
  if (!start.isValid) return null
  const end = start.plus({ minutes: Number(durationInMinutes) || 0 })
  const zone = toThreeLetterTimezone(timezone, start)
  const range = `${start.toFormat("ccc, LLL d '·' h:mm a")} – ${end.toFormat(
    'h:mm a',
  )}`
  return zone ? `${range} ${zone}` : range
}

// Step 1 reads `selectedDay` (an ISO date in the facility's time zone) to open
// on the day the slot was picked, so the pencil returns to that day.
export const buildEditTimeUrl = (
  previousUrl,
  startDateTime,
  timezone,
  includeSelectedDay = true,
) => {
  if (!previousUrl) return previousUrl
  const start = startDateTime
    ? DateTime.fromISO(startDateTime, { zone: timezone })
    : null
  if (!includeSelectedDay || !start || !start.isValid) return previousUrl
  const separator = previousUrl.includes('?') ? '&' : '?'
  return `${previousUrl}${separator}selectedDay=${start.toISODate()}`
}

// Same payload the legacy SelectStudentLayout sends with its GTM events.
export const buildSelectStudentEvent = (lessonType, startDateTime) => {
  const timezone = get(lessonType?.facilities, ['0', 'timezone'])
  const start = DateTime.fromISO(startDateTime, { zone: timezone })
  return {
    event: 'select-student-to-schedule',
    title: lessonType?.title,
    category: lessonType?.category,
    facility: get(lessonType?.facilities, ['0', 'name']),
    location: `${get(lessonType?.facilities, ['0', 'city'])}, ${get(
      lessonType?.facilities,
      ['0', 'state'],
    )}`,
    price: `${lessonType?.priceInCents / 100}`,
    event_date: start.toFormat('yyyy-LL-dd'),
    event_time: start.toFormat('hh:mm a'),
    duration: lessonType?.durationInMinutes,
    coach_id: lessonType?.coach?.id,
    coach_name: lessonType?.coach?.name,
    access_code_required: lessonType?.requiresAccessCode ? 'yes' : 'no',
  }
}

// Mirrors how the legacy flow rewrites the URL before adding a student.
export const withStudentId = (currentUrl, studentId, { stripExisting }) => {
  const base = stripExisting
    ? currentUrl.replace(/&studentId=\d+/, '')
    : currentUrl
  return `${base}&studentId=${studentId}`
}

// Finds the signed-in contact's own student record (relationship SELF).
export const findSelfStudentId = (students, user) =>
  students?.find(student =>
    student?.representatives?.some(
      r =>
        r?.relationshipType === 'SELF' &&
        r?.contact?.externalId === user?.externalId,
    ),
  )?.id
